import { type ReactNode, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { CaptureBar } from "./CaptureBar";
import { BottomNav } from "./BottomNav";
import { ChatPanel } from "@/components/chat/ChatPanel";
import { PullToRefreshIndicator } from "./PullToRefreshIndicator";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";

type Props = {
  children: ReactNode;
  /** Hide TopBar + CaptureBar (for fullscreen conversation views) */
  minimal?: boolean;
};

export function AppLayout({ children, minimal }: Props) {
  const queryClient = useQueryClient();

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries();
  }, [queryClient]);

  const { setRef: setPullRef, pullDistance, isRefreshing, isPastThreshold } =
    usePullToRefresh({ onRefresh: handleRefresh });

  return (
    <div className="flex h-screen bg-background text-foreground">
      <Sidebar />
      <div
        className="flex-1 flex flex-col min-w-0"
        style={{
          paddingTop: "env(safe-area-inset-top)",
        }}
      >
        {!minimal && <TopBar />}
        {!minimal && <CaptureBar />}
        <main
          ref={setPullRef}
          className={`flex-1 overflow-y-auto pb-safe-nav ${minimal ? "" : "px-4 py-4"}`}
          style={{ overscrollBehavior: "contain" }}
        >
          <PullToRefreshIndicator
            pullDistance={pullDistance}
            isRefreshing={isRefreshing}
            isPastThreshold={isPastThreshold}
          />
          {children}
        </main>
        <BottomNav />
        <ChatPanel />
      </div>
    </div>
  );
}
